import { useRef, useState } from 'react';
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { Formik } from 'formik';
import Button from '../components/Button';
import {
  Drawer,
  DrawerExit,
  DrawerLink,
  DrawerList,
  DrawerTrigger,
} from '../components/Drawer';
import Loader from '../components/Loader';
import {
  Modal,
  ModalEditableBody,
  ModalEditableName,
  ModalExit,
  ModalPart,
} from '../components/Modal';
import { useToast } from '../components/Toast';
import { CREATE_TOPIC } from '../utilities/mutations';
import { SPACE_TAGS } from '../utilities/queries';
import { createTopicCache } from '../utilities/cache';
import { useSpaceContext } from '../contexts/space';

const NewTopicModal = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  const toast = useToast();
  const { spaceVars } = useSpaceContext();
  const { loading, data } = useQuery(SPACE_TAGS, {
    variables: { slug: params.s },
  });
  const [createTopic] = useMutation(CREATE_TOPIC);
  const [isUnsaved, setIsUnsaved] = useState(false);
  const [shake, setShake] = useState(false);
  const [searchParams] = useSearchParams();

  const modalRef = useRef();
  const [scrollFix, setScrollFix] = useState(false);
  const [scrollTop, setScrollTop] = useState(0);

  const [submitting, setSubmitting] = useState(false);

  if (!localStorage.getItem('token')) {
    navigate('/login', { state: { from: location }, replace: true });
  }

  const closeModal = () => {
    if (!shake && !submitting) {
      if (isUnsaved) {
        setShake(true);
        toast({ children: `Don't forget to save!` });
        setTimeout(() => setShake(false), 500);
      } else {
        if (location.state?.backgroundLocation) {
          navigate(-1);
        } else {
          navigate(`/s/${params.s}`);
        }
      }
    }
  };

  const close = () => {
    if (location.state?.backgroundLocation) {
      navigate(-1);
    } else {
      navigate(`/s/${params.s}`);
    }
  };

  return (
    <Modal
      ref={modalRef}
      shake={shake}
      onClose={() => closeModal()}
      onScroll={() => {
        if (scrollFix) {
          setScrollFix(false);
          modalRef.current.scrollTop = scrollTop;
        } else {
          setScrollTop(modalRef.current.scrollTop);
        }
      }}
    >
      {loading ? (
        <ModalPart>
          <Loader />
        </ModalPart>
      ) : (
        <>
          <ModalExit onClick={() => close()} dontCloseOnClick />
          <Formik
            initialValues={{ name: '', tags: [], body: '' }}
            validate={(values) => {
              if (values.name !== '' || values.body !== '') {
                setIsUnsaved(true);
              } else {
                setIsUnsaved(false);
              }
            }}
            onSubmit={async (values) => {
              try {
                setSubmitting(true);

                const createTopicData = await createTopic({
                  variables: {
                    name: values.name,
                    body: values.body,
                    spaceId: location.state?.spaceId,
                    tags: values.tags.map((tag) => {
                      return tag;
                    }),
                  },
                  update: (cache, { data: { createTopic } }) =>
                    createTopicCache(
                      location.state?.spaceId,
                      cache,
                      createTopic
                    ),
                });

                navigate(
                  `/s/${location?.state?.spaceSlug}/t/${createTopicData.data.createTopic.publicId}`,
                  {
                    state: {
                      filters: {
                        author: searchParams.get('author'),
                        state: searchParams.get('state'),
                        assignee: searchParams.get('assignee'),
                        tag: searchParams.get('tag'),
                      },
                    },
                  }
                );

                setSubmitting(false);
              } catch (error) {
                toast({ children: error.message });
                setSubmitting(false);

                return;
              }
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
              setFieldValue,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalEditableName
                  type="text"
                  name="name"
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.name}
                  data-autofocus
                />
                <ModalEditableBody
                  type="text"
                  name="body"
                  setScrollFix={setScrollFix}
                  scrollTop={scrollTop}
                  disabled={isSubmitting}
                  onChange={handleChange}
                  onBlur={handleBlur}
                  value={values.body}
                  modalRef={modalRef}
                />
                <ModalPart isFooter>
                  {data?.space?.tags.length !== 0 && (
                    <Drawer>
                      <DrawerTrigger>
                        <Button type="button" disabled={isSubmitting}>
                          {values.tags.length !== 0
                            ? `${values.tags.length} ${
                                values.tags.length === 1 ? 'tag' : 'tags'
                              }`
                            : 'Tags'}
                        </Button>
                      </DrawerTrigger>
                      <DrawerList>
                        <DrawerExit />
                        {data.space.tags.map((tag) => (
                          <DrawerLink
                            as="button"
                            isActive={values.tags.includes(tag.id)}
                            onClick={() => {
                              if (values.tags.includes(tag.id)) {
                                setFieldValue(
                                  'tags',
                                  values.tags.filter(
                                    (innerTagId) => innerTagId !== tag.id
                                  )
                                );
                              } else {
                                setFieldValue('tags', [...values.tags, tag.id]);
                              }
                            }}
                            dontCloseOnClick
                          >
                            {tag.name}
                          </DrawerLink>
                        ))}
                      </DrawerList>
                    </Drawer>
                  )}
                  <Button
                    type="submit"
                    disabled={
                      values.body.trim() === '' ||
                      values.name.trim() === '' ||
                      isSubmitting
                    }
                    isPrimary
                  >
                    Create
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </>
      )}
    </Modal>
  );
};

export default NewTopicModal;
